import { useState } from 'react';
import { last } from 'lodash';
import PropTypes from 'prop-types';
import Calendar from 'components/calendar';
import { useDailyTimetableQuery } from 'redux/api_slice/service';
import AvailableOptions from './available_options';
import { formatQtDate, formatWeekdayDateMini, getPeriodList } from './helpers';
import TableContent from './table_content';
import { useAvailableOptions } from './use_available_options';

const TimetableTwoStops = ({
  duration,
  disableSeasonalities,
  hiddenSeasonalityRouteIds,
}) => {
  const [availableOptions, selectedOption, setSelectedOption] =
    useAvailableOptions();

  const [selectedDate, setSelectedDate] = useState(formatQtDate(new Date()));
  const [dateRangeList, setDateRangeList] = useState(
    getPeriodList(new Date(selectedDate))
  );
  const qtDates = Object.values(dateRangeList);
  const { data: timetableData, isFetching } = useDailyTimetableQuery({
    route_id: selectedOption,
    start_date: qtDates[0],
    end_date: last(qtDates),
  });

  const activeColumn = formatWeekdayDateMini(new Date(selectedDate));

  // Find the selected segment and get its stop IDs for filtering
  const selectedSegment = availableOptions.find(
    (opt) => opt.routeId === selectedOption
  );
  const intendedFromStopId = selectedSegment?.from_stop_id;
  const intendedToStopId = selectedSegment?.to_stop_id;
  const isForwardJourney = selectedSegment?.isForward;

  // Filter timetable data based on journey type:
  // Forward journey: match to_route_stop_id with to_stop_id (or keep if to_route_stop_id is missing)
  // Return journey: match from_route_stop_id with from_stop_id (or keep if from_route_stop_id is missing)
  // Keep entries that don't have stop IDs specified
  const filteredTimetableData = {};
  const shouldFilter = isForwardJourney
    ? intendedToStopId != null
    : intendedFromStopId != null;

  Object.keys(timetableData || {}).forEach((dateKey) => {
    if (!shouldFilter) {
      // No subroutes available, return all data without filtering
      filteredTimetableData[dateKey] = timetableData[dateKey] || [];
    } else {
      filteredTimetableData[dateKey] = (timetableData[dateKey] || []).filter(
        (item) =>
          isForwardJourney
            ? item.to_route_stop_id == null ||
              item.to_route_stop_id === intendedToStopId
            : item.from_route_stop_id == null ||
              item.from_route_stop_id === intendedFromStopId
      );
    }
  });

  disableSeasonalities =
    disableSeasonalities || hiddenSeasonalityRouteIds.includes(selectedOption);

  return (
    <div>
      <AvailableOptions
        options={availableOptions}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
      />
      <div className="d-flex justify-content-between align-items-sm-center py-4 flex-sm-row flex-column-reverse">
        <div className="position-relative flex-grow-1 flex-basis-0 pt-3 pt-sm-0">
          <label htmlFor="date-select" className="text-primary" role="button">
            <i className="far fa-calendar-alt pe-1" />
            {activeColumn}
          </label>
          <Calendar
            inputProps={{
              id: 'date-select',
              className: 'visually-hidden start-0 bottom-0',
              placeholder: 'Please select a date',
            }}
            onChange={(event) => {
              setSelectedDate(formatQtDate(event.value));
              setDateRangeList(getPeriodList(event.value));
            }}
          />
        </div>
        <div className="text-center">
          {duration && (
            <>
              Approximate trip duration
              <h5 className="text-primary">{duration}</h5>
            </>
          )}
        </div>
        <div className="flex-grow-1 flex-basis-0" />
      </div>
      <TableContent
        isFetching={isFetching}
        disableSeasonalities={disableSeasonalities}
        columns={Object.keys(dateRangeList)}
        activeColumn={activeColumn}
        data={filteredTimetableData}
      />
    </div>
  );
};

TimetableTwoStops.propTypes = {
  duration: PropTypes.string,
  disableSeasonalities: PropTypes.bool,
  hiddenSeasonalityRouteIds: PropTypes.arrayOf(PropTypes.number),
};

export default TimetableTwoStops;
